import React from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';

import {
  Modal,
  ModalActions,
  ModalCancelButton,
  ModalHeader,
  ModalSubmitButton,
  NewCheckboxField,
  NewRadioField,
  SectionWithBorder,
  SectionWithBorderField,
  showModal,
} from '@edna/components';
import {FieldError} from '@edna/components/FormField';
import {Icon, Stack} from '@edna/components/primitives';
import {ECurrency} from '@edna/utils';
import {ECurrencySymbol} from '@edna/utils/formatMoney';

import {Text} from 'src/components/primitives';
import {CardIcon, PayKeeperLogoIcon, SBPLogoIcon} from 'src/icons';
import {joiResolver} from 'src/utils/validator';

import {
  useCreatePayKeeperOrderMutation,
  useGetPayKeeperInfoQuery,
  usePayWithPayKeeperMutation,
} from '../api';
import {MIN_PAYKEEPER_AMOUNT} from '../definitions';
import {sendForm} from '../sendPayKeeperForm';
import * as S from '../style';

type TFormData = {
  sum: number;
  isNewCard: boolean;
  isSaveCard?: boolean;
};

const PayKeeperSettingsModal = React.memo<{closeModal: TEmptyFunction}>(({closeModal}) => {
  const {t} = useTranslation();

  const {data: payKeeperInfo} = useGetPayKeeperInfoQuery();
  const [createPayKeeperOrder, {isLoading}] = useCreatePayKeeperOrderMutation();
  const [payWithPayKeeper, {isLoading: isLoadingPayment}] = usePayWithPayKeeperMutation();

  const methods = useForm<TFormData>({
    mode: 'all',
    resolver: joiResolver((joi) =>
      joi.object({
        sum: joi.number().min(MIN_PAYKEEPER_AMOUNT).required(),
      }),
    ),
    defaultValues: {isNewCard: false},
  });

  const sumError = methods.formState.errors.sum;
  const isNewCard = methods.watch('isNewCard');
  const hasSavedCard = !!payKeeperInfo?.maskedCardNumber;

  const handleSave = React.useCallback(
    async (formValues: TFormData) => {
      if (hasSavedCard && !formValues.isNewCard) {
        payWithPayKeeper(formValues.sum);
        closeModal();

        return;
      }

      try {
        const orderInfo = await createPayKeeperOrder({
          sum: formValues.sum,
          isSaveCard: formValues.isSaveCard,
        }).unwrap();

        sendForm(`${orderInfo.paykeeperServerUrl}/create/`, {
          orderid: orderInfo.orderId,
          clientid: orderInfo.clientId,
          client_email: orderInfo.clientEmail,
          service_name: orderInfo.serviceName,
          sum: formValues.sum,
          msgtype: formValues.isSaveCard ? 'createbinding' : null,
        });

        closeModal();
      } catch (e) {}
    },
    [closeModal, hasSavedCard],
  );

  return (
    <Modal width="530px">
      <FormProvider {...methods}>
        <ModalHeader>{t('BalanceReplenishment:payKeeperSettingsModal.title')}</ModalHeader>
        <S.PayKeeperModalContent>
          <Stack direction="ROW" size="6" justify="center" isFullWidth>
            <PayKeeperLogoIcon />
            <SBPLogoIcon />
          </Stack>
          <SectionWithBorderField
            name="sum"
            label={t('BalanceReplenishment:payKeeperSettingsModal.amount')}
            hint={t('BalanceReplenishment:payKeeperSettingsModal.minAmount')}
            htmlFor="sum"
          >
            <Stack size="2" direction="ROW" align="center">
              <S.PayKeeperNumberInputField name="sum" showError={false} />
              <Text size="S">{ECurrencySymbol[ECurrency.RUB]}</Text>
            </Stack>
            {!!sumError && (
              <FieldError>{t(sumError?.message ?? '', sumError?.ref?.value) as string}</FieldError>
            )}
          </SectionWithBorderField>
          {!hasSavedCard && (
            <SectionWithBorder>
              <NewCheckboxField name="isSaveCard">
                {t('BalanceReplenishment:payKeeperSettingsModal.saveCard')}
              </NewCheckboxField>
            </SectionWithBorder>
          )}
          {hasSavedCard && (
            <SectionWithBorder
              label={t('BalanceReplenishment:payKeeperSettingsModal.chooseCard')}
              size="2"
            >
              <NewRadioField name="isNewCard" type="radio" value={false} noMargin>
                {t('BalanceReplenishment:payKeeperSettingsModal.savedCard')}
              </NewRadioField>
              {!isNewCard && (
                <S.NestedBlock>
                  <Text color="grey400" size="XS">
                    <Stack direction="ROW" align="center" size="2">
                      <Icon as={CardIcon} size="16px" />
                      {payKeeperInfo.maskedCardNumber}
                    </Stack>
                  </Text>
                </S.NestedBlock>
              )}
              <NewRadioField name="isNewCard" type="radio" value={true} noMargin>
                {t('BalanceReplenishment:payKeeperSettingsModal.newCard')}
              </NewRadioField>
              {isNewCard && (
                <S.NestedBlock>
                  <NewCheckboxField name="isSaveCard">
                    {t('BalanceReplenishment:payKeeperSettingsModal.replaceCard')}
                  </NewCheckboxField>
                </S.NestedBlock>
              )}
            </SectionWithBorder>
          )}
          <Text size="S" secondary>
            {t('BalanceReplenishment:payKeeperSettingsModal.acceptanceInfo')}
          </Text>
        </S.PayKeeperModalContent>
        <ModalActions>
          <ModalCancelButton onClick={closeModal}>{t('Common:button.cancel2')}</ModalCancelButton>
          <ModalSubmitButton
            onClick={methods.handleSubmit(handleSave)}
            disabled={isLoading || isLoadingPayment}
          >
            {t('BalanceReplenishment:payKeeperSettingsModal.pay')}
          </ModalSubmitButton>
        </ModalActions>
      </FormProvider>
    </Modal>
  );
});

export const showPayKeeperOnetimePaymentSettingsModal = () =>
  showModal((closeModal) => <PayKeeperSettingsModal closeModal={closeModal} />);
